import useSWR from "swr";
import { useContext, useEffect, useState } from "react";

import { UserConfigurationResponse } from "../models/UserModels";
import { useSWRFetcher } from "./useSWRFetcher";
import { environment } from "../config/environment";
import { ToastContext } from "../context/ToastContext";
import { useLocalStorage } from "../utils/localStorage";

interface CachedUserConfiguration {
  data: UserConfigurationResponse;
  timestamp: number;
}

const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export const useUserConfiguration = () => {
  const fetcher = useSWRFetcher();
  const { showToast } = useContext(ToastContext);
  const localStorage = useLocalStorage();
  const [cachedData, setCachedData] = useState<UserConfigurationResponse | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Check cache on mount
  useEffect(() => {
    if (!localStorage) return;

    const cached = localStorage.getItem<CachedUserConfiguration>(localStorage.keys.userConfiguration);
    const now = Date.now();

    if (cached && now - cached.timestamp < CACHE_DURATION) {
      // Cache is valid, use cached data
      setCachedData(cached.data);
    } else {
      // Cache is invalid or doesn't exist, fetch new data
      setShouldFetch(true);
    }
  }, [localStorage]);

  const { data, isLoading, error, mutate } = useSWR<UserConfigurationResponse>(
    shouldFetch ? `${environment.apiUrl}/api/users/configuration` : null,
    (url: string) => fetcher(url),
    {
      onError: () => {
        showToast("Failed to get user configuration, please try again later", "error");
      },
      onSuccess: (data) => {
        // Cache the new data
        if (localStorage && data) {
          const cacheData: CachedUserConfiguration = {
            data,
            timestamp: Date.now(),
          };
          localStorage.setItem(localStorage.keys.userConfiguration, cacheData);
          setCachedData(data);
        }
      },
    }
  );

  const userConfiguration = cachedData || data;

  return {
    userConfiguration,
    isLoading: shouldFetch ? isLoading : false,
    isError: !!error,
    mutate: () => {
      // Clear cache and force refetch
      if (localStorage) {
        localStorage.removeItem(localStorage.keys.userConfiguration);
      }
      setCachedData(null);
      setShouldFetch(true);
      return mutate();
    },
  };
};
